<template>
  <section class="event-modal-equipment-letters">
    <EventModalPassesAddForm
      v-if="editOnly || (hasAccessToWrite && (isFormShown || !letters.length))"
      class="event-modal-equipment-letters__form"
      :show-hide="!editOnly && !!letters.length"
      :is-submitting="isSubmitting"
      :package-upload="false"
      @hide="isFormShown = false"
      @submit="submit"
    >
      <template #form-title>
        {{
          $t(
            editOnly
              ? "event.passes.equipmentLetters.editTitle"
              : "event.passes.equipmentLetters.addTitle"
          )
        }}
      </template>

      <h2 class="event-modal-passes-add-form__title">
        {{ $t("event.passes.equipmentLetters.customerTitle") }}
      </h2>

      <div class="event-modal-passes-add-form__row flex">
        <div class="event-modal-passes-add-form__row-col _380">
          <InputLabel>
            <template #label>
              {{ $t("event.passes.equipmentLetters.carrierCompany") }}
            </template>
            <input
              v-model.trim="form.carrierCompanyName"
              name="carrierCompanyName"
              type="text"
              v-validate="'required|max:255'"
            />
            <template #error v-if="errors.has('carrierCompanyName')">
              {{ errors.first("carrierCompanyName") }}
            </template>
          </InputLabel>
        </div>

        <div class="event-modal-passes-add-form__row-col _380">
          <InputLabel>
            <template #label>
              {{ $t("event.passes.equipmentLetters.carrierCountry") }}
            </template>
            <input
              v-model.trim="form.carrierCountry"
              name="carrierCountry"
              type="text"
              v-validate="'required|max:100'"
            />
            <template #error v-if="errors.has('carrierCountry')">
              {{ errors.first("carrierCountry") }}
            </template>
          </InputLabel>
        </div>
      </div>

      <EventModalStandFields
        v-model="form.stand"
        :stands="stands"
        :error="standError"
        @input="standError = ''"
      />

      <h2 class="event-modal-passes-add-form__title">
        {{ $t("event.passes.equipmentLetters.itemsTitle") }}
      </h2>

      <div
        class="event-modal-passes-add-form__row flex"
        v-for="item in form.exhibits"
        :key="item.id"
      >
        <div class="event-modal-passes-add-form__row-col _600">
          <InputLabel>
            <template #label>
              {{ $t("event.passes.equipmentLetters.itemTitle") }}
            </template>
            <input
              v-model.trim="item.title"
              :name="`exhibitTitle${item.id}`"
              type="text"
              v-validate="'required|max:255'"
            />
            <template #error v-if="errors.has(`exhibitTitle${item.id}`)">
              {{ errors.first(`exhibitTitle${item.id}`) }}
            </template>
          </InputLabel>
        </div>

        <div
          class="event-modal-passes-add-form__row-col _160 event-modal-equipment-letters__item-quantity"
        >
          <InputLabel>
            <template #label>
              {{ $t("event.passes.equipmentLetters.quantity") }}
            </template>
            <input
              v-model="item.quantity"
              :name="`exhibitQuantity${item.id}`"
              type="number"
              min="1"
              max="100000"
              :data-vv-as="$t('event.passes.equipmentLetters.quantity')"
              v-validate="'required|numeric|min_value:1|max_value:100000'"
            />
            <template #error v-if="errors.has(`exhibitQuantity${item.id}`)">
              {{ $t("event.passes.equipmentLetters.quantityInvalid") }}
            </template>
          </InputLabel>

          <button
            type="button"
            class="event-modal-equipment-letters__item-delete"
            :aria-label="$t('event.passes.equipmentLetters.deleteItem')"
            @click="removeItem(item.id)"
          >
            <icon name="delete" />
          </button>
        </div>
      </div>

      <div class="event-modal-passes-add-form__row">
        <LinkIcon
          v-if="form.exhibits.length < 100"
          icon="add"
          :bold="true"
          :grey="true"
          @click="addItem"
        >
          {{ $t("event.passes.equipmentLetters.addItem") }}
        </LinkIcon>
      </div>

      <p
        v-if="itemsError"
        class="text-error event-modal-equipment-letters__items-error"
      >
        {{ itemsError }}
      </p>

      <h2 class="event-modal-passes-add-form__title">
        {{ $t("event.passes.equipmentLetters.signerTitle") }}
      </h2>

      <div class="event-modal-passes-add-form__row flex">
        <div class="event-modal-passes-add-form__row-col _380">
          <InputLabel>
            <template #label>
              {{ $t("event.passes.equipmentLetters.signerFullName") }}
            </template>
            <input
              v-model.trim="form.signerFullName"
              name="signerFullName"
              type="text"
              v-validate="'required|max:255'"
            />
            <template #error v-if="errors.has('signerFullName')">
              {{ errors.first("signerFullName") }}
            </template>
          </InputLabel>
        </div>

        <div class="event-modal-passes-add-form__row-col _380">
          <InputLabel>
            <template #label>
              {{ $t("event.passes.equipmentLetters.signerPosition") }}
            </template>
            <input
              v-model.trim="form.signerPosition"
              name="signerPosition"
              type="text"
              v-validate="'required|max:255'"
            />
            <template #error v-if="errors.has('signerPosition')">
              {{ errors.first("signerPosition") }}
            </template>
          </InputLabel>
        </div>
      </div>

      <h2 class="event-modal-passes-add-form__title">
        {{ $t("event.passes.equipmentLetters.responsibleTitle") }}
      </h2>

      <div class="event-modal-passes-add-form__row flex">
        <div class="event-modal-passes-add-form__row-col _380">
          <InputLabel>
            <template #label>
              {{ $t("event.passes.equipmentLetters.responsibleFullName") }}
            </template>
            <input
              v-model.trim="form.responsibleFullName"
              name="responsibleFullName"
              type="text"
              v-validate="'required|max:255'"
            />
            <template #error v-if="errors.has('responsibleFullName')">
              {{ errors.first("responsibleFullName") }}
            </template>
          </InputLabel>
        </div>

        <div class="event-modal-passes-add-form__row-col _380">
          <InputLabel>
            <template #label>
              {{ $t("event.passes.equipmentLetters.responsiblePhone") }}
            </template>
            <input
              v-model="form.responsiblePhone"
              v-mask="'+#############################'"
              name="responsiblePhone"
              type="text"
              placeholder="+79000000000"
              v-validate="'required|phone|max:30'"
            />
            <template #error v-if="errors.has('responsiblePhone')">
              {{ errors.first("responsiblePhone") }}
            </template>
          </InputLabel>
        </div>
      </div>
    </EventModalPassesAddForm>

    <div v-if="!editOnly" class="event-modal-equipment-letters__list">
      <div class="event-modal-equipment-letters__list-header">
        <LinkIcon
          v-if="hasAccessToWrite && letters.length && !isFormShown"
          icon="add"
          :bold="true"
          @click="showForm"
        >
          {{ $t("event.passes.equipmentLetters.addButton") }}
        </LinkIcon>

        <button
          type="button"
          class="event-modal-equipment-letters__reload"
          @click="load(currentPage)"
        >
          {{ $t("event.passes.reload") }}
        </button>
      </div>

      <div
        class="event-modal-equipment-letters__table-wrap"
        v-if="letters.length"
      >
        <table class="event-modal-equipment-letters__table">
          <thead>
            <tr>
              <th>{{ $t("event.passes.equipmentLetters.carrier") }}</th>
              <th>{{ $t("event.passes.equipmentLetters.createdAt") }}</th>
              <th class="event-modal-equipment-letters__actions-col"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="letter in letters" :key="letter.id">
              <td>
                {{ letter.carrier_company_name }}
                <small>{{ letter.stand || "—" }}</small>
              </td>
              <td>{{ letter.created_at || "—" }}</td>
              <td>
                <div class="event-modal-equipment-letters__actions">
                  <button
                    v-if="hasAccessToWrite"
                    type="button"
                    class="event-modal-equipment-letters__actions-item"
                    @click="openEditModal(letter)"
                    :aria-label="$t('common.editBtn')"
                  >
                    <Tooltip :label="$t('common.editBtn')">
                      <icon name="link-icon-edit" width="16" height="16" />
                    </Tooltip>
                  </button>

                  <button
                    v-if="hasAccessToWrite"
                    type="button"
                    class="event-modal-equipment-letters__actions-item"
                    @click="copyLetter(letter)"
                    :aria-label="$t('event.documents.copyFile')"
                  >
                    <Tooltip :label="$t('event.documents.copyFile')">
                      <icon name="copy" width="16" height="16" />
                    </Tooltip>
                  </button>

                  <button
                    type="button"
                    class="event-modal-equipment-letters__actions-item"
                    @click="downloadLetter(letter)"
                    :aria-label="$t('event.documents.downloadFileShort')"
                  >
                    <Tooltip :label="$t('event.documents.downloadFileShort')">
                      <icon name="download-grey" width="16" height="16" />
                    </Tooltip>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p v-else-if="!isLoading" class="event-modal-equipment-letters__empty">
        {{ $t("event.passes.equipmentLetters.empty") }}
      </p>

      <Pagination
        v-if="lastPage > 1"
        class="event-modal-equipment-letters__pagination"
        :page="currentPage"
        :page-count="lastPage"
        :total-visible="paginationPageAmount"
        @change="load"
      />

      <Loader v-if="isLoading" white opacity is-absolute />
    </div>
  </section>
</template>

<script>
import apiEquipmentLetter from "@/api/equipmentLetter";
import getError from "@/helpers/error";
import {
  InputLabel,
  LinkIcon,
  Loader,
  Pagination,
  Tooltip
} from "@v2/shared/ui/components";
import EventModalPassesAddForm from "./EventModalPassesAddForm.vue";
import EventModalStandFields from "./EventModalStandFields.vue";

let itemId = 0;
const newItem = () => ({ id: ++itemId, title: "", quantity: 1 });

export default {
  name: "EventModalEquipmentLetters",
  components: {
    EventModalPassesAddForm,
    EventModalStandFields,
    InputLabel,
    LinkIcon,
    Loader,
    Pagination,
    Tooltip
  },
  props: {
    participantId: {
      type: Number,
      required: true
    },
    stands: {
      type: Array,
      default: () => []
    },
    hasAccessToWrite: {
      type: Boolean,
      default: false
    },
    participantCompanyName: {
      type: String,
      default: ""
    },
    participantCountry: {
      type: String,
      default: ""
    },
    editOnly: {
      type: Boolean,
      default: false
    },
    editLetter: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      letters: [],
      currentPage: 1,
      lastPage: 1,
      isLoading: false,
      isSubmitting: false,
      isFormShown: false,
      standError: "",
      itemsError: "",
      form: this.createForm()
    };
  },
  computed: {
    paginationPageAmount() {
      return window.innerWidth > 570 ? 5 : 2;
    },
    payload() {
      return {
        stand_id: this.form.stand?.id,
        carrier_company_name: this.form.carrierCompanyName,
        carrier_country: this.form.carrierCountry,
        signer_full_name: this.form.signerFullName,
        signer_position: this.form.signerPosition,
        responsible_full_name: this.form.responsibleFullName,
        responsible_phone: this.form.responsiblePhone,
        exhibits: this.form.exhibits.map(this.serializeItem)
      };
    }
  },
  mounted() {
    if (!this.editOnly) this.load();
  },
  methods: {
    createForm(letter = this.editLetter) {
      if (letter) {
        return {
          stand:
            this.stands.find(stand => stand.title === letter.stand) || null,
          carrierCompanyName: letter.carrier_company_name,
          carrierCountry: letter.carrier_country,
          signerFullName: letter.signer_full_name,
          signerPosition: letter.signer_position,
          responsibleFullName: letter.responsible_full_name,
          responsiblePhone: letter.responsible_phone,
          exhibits: letter.exhibits.map(item => ({
            id: ++itemId,
            title: item.title,
            quantity: item.quantity
          }))
        };
      }

      return {
        stand: this.stands.length === 1 ? this.stands[0] : null,
        carrierCompanyName: this.participantCompanyName,
        carrierCountry: this.participantCountry,
        signerFullName: "",
        signerPosition: "",
        responsibleFullName: "",
        responsiblePhone: "",
        exhibits: [newItem()]
      };
    },
    openEditModal(letter) {
      this.$modal.show("event-modal-edit-equipment-letter", {
        participantId: this.participantId,
        stands: this.stands,
        letter,
        onSaved: () => this.load(this.currentPage)
      });
    },
    serializeItem(item) {
      return { title: item.title, quantity: Number(item.quantity) };
    },
    addItem() {
      this.form.exhibits.push(newItem());
      this.itemsError = "";
    },
    removeItem(id) {
      this.form.exhibits = this.form.exhibits.filter(item => item.id !== id);
    },
    showForm() {
      this.isFormShown = true;
      this.$nextTick(() => {
        this.$validator.reset();
        this.$validator.resume();
      });
    },
    copyLetter(letter) {
      this.form = this.createForm(letter);
      this.standError = "";
      this.itemsError = "";
      this.showForm();
    },
    async load(page = 1) {
      this.isLoading = true;
      try {
        const response = await apiEquipmentLetter.getAll(
          this.participantId,
          page
        );
        this.letters = response?.data || [];
        this.currentPage = response?.meta?.current_page || page;
        this.lastPage = response?.meta?.last_page || 1;
      } catch (error) {
        this.$store.dispatch("common/setErrorMessage", getError(error));
      } finally {
        this.isLoading = false;
      }
    },
    async submit() {
      const valid = await this.$validator.validateAll();
      this.standError = this.form.stand
        ? ""
        : this.$t("event.passes.equipmentLetters.standRequired");
      this.itemsError = this.form.exhibits.length
        ? ""
        : this.$t("event.passes.equipmentLetters.itemsRequired");

      if (!valid || this.standError || this.itemsError) return;

      this.isSubmitting = true;
      try {
        if (this.editOnly) {
          await apiEquipmentLetter.update(
            this.participantId,
            this.editLetter.id,
            this.payload
          );
          this.$emit("saved");
          return;
        }

        await apiEquipmentLetter.add(this.participantId, this.payload);
        this.currentPage = 1;
        await this.load();
        this.form = this.createForm();
        this.isFormShown = false;
        this.$validator.reset();
      } catch (error) {
        this.$store.dispatch("common/setErrorMessage", getError(error));
      } finally {
        this.isSubmitting = false;
      }
    },
    async downloadLetter(letter) {
      this.isLoading = true;
      try {
        const response = await apiEquipmentLetter.download(
          this.participantId,
          letter.id
        );
        const url = window.URL.createObjectURL(
          new Blob([response], { type: "application/pdf" })
        );
        const link = document.createElement("a");
        link.href = url;
        link.download = `${this.$t("event.passes.equipmentLetters.fileName")}-${
          letter.id
        }.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
      } catch (error) {
        this.$store.dispatch("common/setErrorMessage", getError(error));
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.event-modal-equipment-letters {
  margin-bottom: 40px;

  &__form {
    background: rgba(#f9f9f9, 0.5);

    @include respond-down(lg) {
      .event-modal-passes-add-form__title {
        display: block;
      }
    }
  }

  &__item-quantity {
    display: flex;
    align-items: flex-start;
  }

  &__item-delete,
  &__actions button,
  &__reload {
    @include button-clean();
  }

  &__item-delete {
    margin: 40px 0 0 20px;

    .svg-icon {
      width: 16px;
      height: 16px;
    }
  }

  &__items-error {
    margin: -16px 0 32px;
  }

  &__list {
    position: relative;
    width: 780px;
    min-height: 100px;
    margin: 40px 0 40px 80px;

    @include respond-down(lg) {
      box-sizing: border-box;
      width: 100%;
      margin: 32px 0;
      padding: 0 24px;
    }

    @include respond-down(md) {
      padding: 0 16px;
    }
  }

  &__list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;

    @include respond-down(md) {
      padding: 0 16px;
    }
  }

  &__reload {
    margin-left: auto;
    color: #a3a8b3;
    font-size: 12px;
  }

  &__table {
    width: 100%;
    border-spacing: 0;
    border-collapse: collapse;

    th,
    td {
      border-top: 1px solid rgba(205, 206, 208, 0.5);
      border-bottom: 1px solid rgba(205, 206, 208, 0.5);
      padding: 0 24px 0 0;
    }

    th {
      padding: 15px 0;
      text-align: left;
      font-size: 12px;
      font-weight: normal;
      line-height: 1.33;
      color: #a3a8b3;

      @include respond-down(md) {
        padding: 8px 16px;
        font-size: 11px;
      }
    }

    td {
      padding: 22px 5px 22px 0;
      color: #000000;
      font-size: 16px;

      @include respond-down(md) {
        padding: 17px 16px;
        font-size: 12px;
      }
    }

    small {
      display: block;
      color: #a3a8b3;
      line-height: 1.5;
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    padding-right: 0;
    text-align: right;

    .svg-icon--link-icon-edit {
      filter: grayscale(1);
    }

    &-item {
      position: relative;
    }

    &-item:not(:first-child) {
      margin-left: 40px;

      @include respond-down(md) {
        margin-left: 15px;
      }
    }
  }

  &__actions-col {
    width: 180px;
  }

  &__empty {
    padding: 24px 0;
    color: #a3a8b3;
    text-align: center;
  }

  &__pagination {
    margin: 30px auto 0;
  }

  @include respond-down(md) {
    &__list-header {
      flex-wrap: wrap;
    }
  }
}
</style>
